import React, { useEffect, useRef, useState } from 'react';
import './GamePlay.css';
import coin from '../../../assets/coin.svg';
import partnerAvatar from '../../../assets/partner.png';
// import RoundHistoryReport from './RoundHistoryReport'; // restore with Last Rounds Summary panel
import { TOTAL_GAME_ROUNDS } from '../../../gameConfig';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

interface GamePlayProps {
  roundNumber: number;
  timeLeft: number;
  prolificId: string;
  disabled?: boolean;
  playerTotalEarnings: number;
  /** Total USD from backend checkPlayerStatus.usdEarnings.totalUsd */
  performancePaymentUsd?: number | null;
  playerEarningsHistory: number[];
  isChatPeriod?: boolean;
  initialTokens: number;
}

type RoundResponse = {
  playerEarnings: number;
  partnerEarnings: number;
  newRoundIndex: number;
};

const GamePlay: React.FC<GamePlayProps> = ({
  roundNumber,
  timeLeft,
  prolificId,
  disabled = false,
  playerTotalEarnings,
  performancePaymentUsd = null,
  playerEarningsHistory,
  isChatPeriod = false,
  initialTokens = 30,
}) => {
  // Default slider to half of initial tokens
  const [sliderValue, setSliderValue] = useState<number>(Math.floor(initialTokens / 2));
  const [isInvested, setIsInvested] = useState<boolean>(false);
  const [isInvesting, setIsInvesting] = useState<boolean>(false);
  const [playerEarnings, setPlayerEarnings] = useState<number>(0);
  const [partnerEarnings, setPartnerEarnings] = useState<number>(0);
  const [roundIndex, setRoundIndex] = useState<number>(roundNumber);
  const [autoInvestAttempts, setAutoInvestAttempts] = useState<number>(0);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const autoInvestRetryRef = useRef<NodeJS.Timeout | null>(null);
  const isInvestingRef = useRef<boolean>(false);

  // Log component initialization
  useEffect(() => {
    console.log('🎮 [GamePlay] Component initialized');
    console.log('[GamePlay] Player ID:', prolificId);
    console.log('[GamePlay] API Base URL:', API_BASE_URL);
    console.log('[GamePlay] Initial round:', roundNumber);
    console.log('[GamePlay] Is chat period:', isChatPeriod);
  }, []); // Run once on mount

  // Track round changes and reset investment state
  useEffect(() => {
    console.log('🔄 [GamePlay] Round changed to:', roundNumber);
    console.log('[GamePlay] Initial tokens for this round:', initialTokens);
    console.log('[GamePlay] Resetting investment state');
    console.log('[GamePlay] Previous isInvested:', isInvested);
    
    // Clear any pending auto-invest retries when round changes
    if (autoInvestRetryRef.current) {
      clearTimeout(autoInvestRetryRef.current);
      autoInvestRetryRef.current = null;
    }
    
    setIsInvested(false);
    setIsInvesting(false);
    isInvestingRef.current = false;
    setSliderValue(Math.floor(initialTokens / 2)); // Reset to half of initial tokens
    setRoundIndex(roundNumber);
    setAutoInvestAttempts(0);
    console.log('[GamePlay] State reset complete - Ready for new investments');
  }, [roundNumber, initialTokens]);

  // Monitor timer and trigger automatic investment when it reaches 0
  useEffect(() => {
    console.log(`⏱️ [GamePlay] Timer update - Time left: ${timeLeft}s, isInvested: ${isInvested}, disabled: ${disabled}, isChatPeriod: ${isChatPeriod}`);
    
    // CRITICAL: Don't auto-invest if:
    // 1. Already invested
    // 2. Disabled
    // 3. In chat period (can't invest during chat)
    // 4. Timer is 0 but round might already be completed
    if (timeLeft === 0 && !isInvested && !disabled && !isChatPeriod) {
      console.log('🚨 [Auto-Investment] TIMER REACHED 0 - Triggering automatic investment');
      console.log('[Auto-Investment] Player ID:', prolificId);
      console.log('[Auto-Investment] Round Index:', roundIndex);
      console.log('[Auto-Investment] Current slider value:', sliderValue);
      console.log('[Auto-Investment] Disabled state:', disabled);
      console.log('[Auto-Investment] Is chat period:', isChatPeriod);
      handleAutoInvestWithRetry();
    } else if (timeLeft === 0 && isInvested) {
      console.log('✅ [Auto-Investment] Timer at 0 but already invested - skipping');
    } else if (timeLeft === 0 && isChatPeriod) {
      console.log('✅ [Auto-Investment] Timer at 0 but in chat period - skipping auto-investment');
    }
  }, [timeLeft, isInvested, disabled, isChatPeriod]);

  // Cleanup auto-invest retry timer on unmount
  useEffect(() => {
    return () => {
      if (autoInvestRetryRef.current) {
        clearTimeout(autoInvestRetryRef.current);
        autoInvestRetryRef.current = null;
      }
    };
  }, []);

  const handleInvest = async () => {
    if (isInvested || isInvestingRef.current) {
      console.log('[Investment] Already invested or in progress, skipping');
      return true;
    }

    isInvestingRef.current = true;
    setIsInvesting(true);
    try {
      // Detailed logging BEFORE API call
      console.log('═══════════════════════════════════════════════');
      console.log('[Investment] START - Submitting investment');
      console.log('[Investment] Player ID:', prolificId);
      console.log('[Investment] Round Index:', roundIndex);
      console.log('[Investment] Amount to invest:', sliderValue);
      console.log('[Investment] API URL:', `${API_BASE_URL}/invest/`);
      console.log('[Investment] Timestamp:', new Date().toISOString());
      console.log('═══════════════════════════════════════════════');

      const requestBody = { amountInvested: sliderValue };
      console.log('[Investment] Request body:', JSON.stringify(requestBody));

      const response = await fetch(`${API_BASE_URL}/invest/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'prolific-id': prolificId 
        },
        body: JSON.stringify(requestBody),
      });

      console.log('[Investment] Response received - Status:', response.status);
      console.log('[Investment] Response headers:', Object.fromEntries(response.headers.entries()));

      // CRITICAL: Check if the API call actually succeeded
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ [Investment] Server REJECTED investment');
        console.error('[Investment] Error status:', response.status);
        console.error('[Investment] Error data:', errorData);
        console.error('═══════════════════════════════════════════════');
        
        // For manual investments, show alert
        if (timeLeft > 0) {
          alert(`Failed to submit investment: ${errorData.error || 'Server error'}. Please try again.`);
        }
        return false; // Return failure
      }

      // Parse successful response
      const responseData = await response.json().catch(() => ({}));
      console.log('✅ [Investment] Server ACCEPTED investment');
      console.log('[Investment] Response data:', responseData);
      console.log('[Investment] Setting isInvested to TRUE');
      console.log('═══════════════════════════════════════════════');

      // Only mark as invested after successful response
      setIsInvested(true);
      return true; // Return success
    } catch (error) {
      console.error('❌ [Investment] NETWORK ERROR during investment');
      console.error('[Investment] Error type:', error instanceof Error ? error.name : typeof error);
      console.error('[Investment] Error message:', error instanceof Error ? error.message : String(error));
      console.error('[Investment] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      console.error('═══════════════════════════════════════════════');
      
      // For manual investments, show alert
      if (timeLeft > 0) {
        alert('Network error: Failed to submit investment. Please check your connection and try again.');
      }
      return false;
    } finally {
      isInvestingRef.current = false;
      setIsInvesting(false);
    }
  };

  // Auto-investment with retry logic when timer expires. If the participant didn't submit, we
  // treat them as "neutral" by auto-investing the MIDDLE of the allowed range (half of this
  // round's initial tokens) rather than 0 (e.g. 30 coins → auto-invest 15).
  const handleAutoInvestWithRetry = async () => {
    // CRITICAL: Check if already invested FIRST (before any async operations)
    if (isInvested) {
      console.log('[Auto-Investment Retry] Already invested, skipping');
      return;
    }

    // CRITICAL: Don't try to invest if we're in chat period
    if (isChatPeriod) {
      console.log('[Auto-Investment Retry] In chat period, skipping auto-investment');
      return;
    }

    // CRITICAL: If we've already had a roundCompleted error, don't retry
    // Check if there's a pending retry that might have been scheduled
    if (autoInvestRetryRef.current) {
      console.log('[Auto-Investment Retry] There is already a pending retry - checking if we should cancel it');
      // Don't cancel here - let the retry handler check for roundCompleted
    }

    const MAX_RETRIES = 5;
    const RETRY_DELAY = 1000; // 1 second between retries

    // Default auto-invest amount is half of the round's initial tokens (not 0).
    const autoInvestAmount = Math.floor((initialTokens || 0) / 2);

    console.log(`[Auto-Investment Retry] Timer expired - Auto-investing ${autoInvestAmount} tokens (half of initial ${initialTokens})`);
    console.log(`[Auto-Investment Retry] Attempt ${autoInvestAttempts + 1}/${MAX_RETRIES}`);
    console.log(`[Auto-Investment Retry] Round Index: ${roundIndex}, Is Chat Period: ${isChatPeriod}`);
    console.log(`[Auto-Investment Retry] Current isInvested state: ${isInvested}`);
    
    // Auto-invest half of initial tokens when timer expires and player hasn't invested
    try {
      const response = await fetch(`${API_BASE_URL}/invest/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'prolific-id': prolificId 
        },
        body: JSON.stringify({ amountInvested: autoInvestAmount }),
      });
      
      if (response.ok) {
        console.log(`[Auto-Investment Retry] Auto-investment of ${autoInvestAmount} successful`);
        setIsInvested(true);
        setAutoInvestAttempts(0);
        // Clear any pending retries
        if (autoInvestRetryRef.current) {
          clearTimeout(autoInvestRetryRef.current);
          autoInvestRetryRef.current = null;
        }
      } else {
        let errorData: { roundCompleted?: boolean | string; error?: string; [key: string]: any } = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // If response isn't JSON, try to get text
          const text = await response.text().catch(() => '');
          console.error('[Auto-Investment Retry] Failed to parse error response as JSON:', text);
          // Try to parse text as JSON if it looks like JSON
          if (text.trim().startsWith('{')) {
            try {
              errorData = JSON.parse(text);
            } catch (parseError) {
              console.error('[Auto-Investment Retry] Failed to parse text as JSON:', parseError);
            }
          }
        }
        
        console.error(`[Auto-Investment Retry] Auto-investment of ${autoInvestAmount} failed:`, errorData);
        console.error('[Auto-Investment Retry] Response status:', response.status);
        console.error('[Auto-Investment Retry] Error data keys:', Object.keys(errorData));
        console.error('[Auto-Investment Retry] errorData.roundCompleted:', errorData.roundCompleted);
        console.error('[Auto-Investment Retry] errorData.error:', errorData.error);
        
        // CRITICAL: If round is completed, don't retry - just stop
        // Check multiple possible indicators of round completion
        const isRoundCompleted = errorData.roundCompleted === true || 
                                 errorData.roundCompleted === 'true' ||
                                 (typeof errorData.error === 'string' && (
                                   errorData.error.includes('already been completed') ||
                                   errorData.error.includes('round that has already been completed') ||
                                   errorData.error.toLowerCase().includes('completed')
                                 )) ||
                                 (response.status === 400 && typeof errorData.error === 'string' && 
                                  errorData.error.toLowerCase().includes('completed'));
        
        console.error('[Auto-Investment Retry] isRoundCompleted check result:', isRoundCompleted);
        
        if (isRoundCompleted) {
          console.log('[Auto-Investment Retry] ⛔⛔⛔ ROUND ALREADY COMPLETED - IMMEDIATELY STOPPING ALL AUTO-INVESTMENT ATTEMPTS ⛔⛔⛔');
          console.log('[Auto-Investment Retry] Error details:', JSON.stringify(errorData, null, 2));
          
          // IMMEDIATELY clear any pending retries BEFORE state updates
          if (autoInvestRetryRef.current) {
            console.log('[Auto-Investment Retry] Clearing pending retry timer');
            clearTimeout(autoInvestRetryRef.current);
            autoInvestRetryRef.current = null;
          }
          
          // Mark as invested to prevent further attempts
          setIsInvested(true);
          setAutoInvestAttempts(0);
          
          console.log('[Auto-Investment Retry] State updated - no more retries will occur');
          return; // STOP - don't retry
        }
        
        // Only retry if round is NOT completed
        const nextAttempt = autoInvestAttempts + 1;
        
        if (nextAttempt < MAX_RETRIES) {
          console.log(`[Auto-Investment Retry] Scheduling retry ${nextAttempt + 1}/${MAX_RETRIES} in ${RETRY_DELAY}ms`);
          setAutoInvestAttempts(nextAttempt);
          autoInvestRetryRef.current = setTimeout(() => {
            // Double-check before retrying
            if (!isInvested && !isChatPeriod) {
              handleAutoInvestWithRetry();
            } else {
              console.log('[Auto-Investment Retry] Skipping retry - state changed (isInvested or isChatPeriod)');
            }
          }, RETRY_DELAY);
        } else {
          console.error(`[Auto-Investment Retry] MAX RETRIES REACHED - Auto-investment of ${autoInvestAmount} failed permanently`);
          setAutoInvestAttempts(0); // Reset attempts
        }
      }
    } catch (error) {
      console.error(`[Auto-Investment Retry] Network error during auto-investment of ${autoInvestAmount}:`, error);
      const nextAttempt = autoInvestAttempts + 1;
      setAutoInvestAttempts(nextAttempt);
      
      if (nextAttempt < MAX_RETRIES) {
        console.log(`[Auto-Investment Retry] Scheduling retry ${nextAttempt + 1}/${MAX_RETRIES} in ${RETRY_DELAY}ms`);
        autoInvestRetryRef.current = setTimeout(() => {
          handleAutoInvestWithRetry();
        }, RETRY_DELAY);
      } else {
        console.error('[Auto-Investment Retry] MAX RETRIES REACHED - Network error persisted');
      }
    }
  };

  const handleNextRound = async () => {
    const tryNextRound = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/nextRound/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });

        if (res.status === 400) {
          const data = await res.json();
          if (data.message === "Must wait for other player's investment") {
            pollingRef.current = setTimeout(tryNextRound, 1000);
          }
        } else if (res.ok) {
          const data: RoundResponse = await res.json();
          setPlayerEarnings(data.playerEarnings);
          setPartnerEarnings(data.partnerEarnings);
          setRoundIndex(data.newRoundIndex);
        } else {
          console.error('Unexpected response:', await res.text());
        }
      } catch (err) {
        console.error('Error calling nextRound:', err);
      }
    };

    tryNextRound();
  };

  const showInvestReminder =
    !isInvested && !isInvesting && !disabled && timeLeft > 0 && timeLeft <= 10;

  // Show round history report during chat periods
  if (isChatPeriod) {
    /*
     * Last Rounds Summary (hidden during debate — engagement notice moved to chat panel top).
     *
     * <div className="game-play">
     *   <RoundHistoryReport
     *     prolificId={prolificId}
     *     playerPerformancePaymentUsd={performancePaymentUsd}
     *   />
     * </div>
     *
     * Previously showed:
     * - Last Rounds Summary header
     * - DebateEngagementNotice (✨ bonus / engagement bullet)
     * - Round history charts OR "No round history available yet" empty state
     */
    return null;
  }

  return (
    <div className="game-play">
      <div className="gameplay-panel-header">Game Play</div>
      
      {/* Debug Info - Commented out */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{
          padding: '8px',
          margin: '8px',
          backgroundColor: '#f0f0f0',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '11px',
          fontFamily: 'monospace'
        }}>
          <div>🔧 Debug Info:</div>
          <div>Player: {prolificId}</div>
          <div>API: {API_BASE_URL || '❌ NOT SET'}</div>
          <div>Round: {roundIndex + 1} | Invested: {isInvested ? '✅' : '❌'}</div>
        </div>
      )} */}

      <div className="game-content">
        <div className="stats-container new-stats-layout stats-container--partner-timer">
          <div className="stat-box gameplay-opponent-stat-box">
            <img src={partnerAvatar} alt="" className="gameplay-opponent-stat-icon" />
            <div className="gameplay-opponent-stat-label">Partner</div>
            <div className="gameplay-opponent-stat-sublabel">Matched player</div>
          </div>
          <div className="stat-box timer-box">
            <div className="stat-value">{timeLeft}</div>
            <div className="stat-label">Time Left</div>
          </div>
          {/* <div className="stat-box your-total-earning-box">
            <div className="stat-value stat-value-earnings">
              <span className="stat-coins">
                {playerTotalEarnings}
                <span className="coin-icon">
                  <img src={coin} alt="coin" style={{ height: '1em', verticalAlign: 'middle' }} />
                </span>
              </span>
              {typeof performancePaymentUsd === 'number' && (
                <span className="stat-usd">(${performancePaymentUsd.toFixed(2)})</span>
              )}
            </div>
            <div className="stat-label">Total Earning</div>
          </div> */}
        </div>

        <div className="investment-container">
          <div className="investment-header">
            <span>Round Investment</span>
            <div className="investment-value">
              <input
                type="text"
                className="investment-input"
                value={sliderValue}
                readOnly
              />
              <span className="coin-icon"><img src={coin} alt="coin" style={{height: '1em', verticalAlign: 'middle'}} /></span>
            </div>
          </div>

          <div className="slider-container" style={{ padding: '0 10px' }}>
            <div
              className="slider-filled"
              style={{
                width: `calc(${(sliderValue / initialTokens) * 100}% + 0.5px)`,
              }}
            ></div>
            <input
              type="range"
              min={0}
              max={initialTokens}
              step={1}
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="investment-slider"
              disabled={isInvested || isInvesting || disabled}
              style={{
                width: 'calc(100% + 20px)',
                marginLeft: '-10px',
                marginRight: '-10px',
              }}
            />
            <div className="slider-labels">
              <div
                className="slider-ticks"
                style={{
                  position: 'relative',
                  width: '100%',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  boxSizing: 'border-box',
                }}
              >
                {[...Array(initialTokens + 1)].map((_, idx) => {
                  const left = (idx / initialTokens) * 100;
                  
                  // Adaptive label spacing based on initial tokens
                  let showLabel = false;
                  if (initialTokens <= 15) {
                    // For small ranges (15 or less), show every number
                    showLabel = true;
                  } else if (initialTokens <= 25) {
                    // For medium ranges (16-25), show every 2
                    showLabel = idx % 2 === 0;
                  } else {
                    // For larger ranges (26+), show every 5
                    showLabel = idx % 5 === 0;
                  }
                  
                  // Always show major ticks (every 5) for visual reference
                  const isMajorTick = idx % 5 === 0 || idx === initialTokens;
                  
                  return (
                    <div
                      className="tick-mark"
                      key={idx}
                      style={{
                        position: 'absolute',
                        left: `calc(${left}% - 1px)`,
                        alignItems: 'center',
                        width: '0',
                      }}
                    >
                      <div
                        className="tick-line"
                        style={{
                          height: isMajorTick ? 10 : 6,
                          backgroundColor:
                            isMajorTick ? '#6B7280' : '#9CA3AF',
                        }}
                      ></div>
                      {showLabel && (
                        <div
                          className="tick-value"
                          style={{ marginTop: 4, fontSize: 10 }}
                        >
                          {idx}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="slider-descriptions">
                <span className="slider-desc-left">Less at stake, less to gain</span>
                <span className="slider-desc-right">More at stake, more to gain</span>
              </div>
            </div>
          </div>

          <div className="invest-button-wrap">
            {showInvestReminder && (
              <div className="invest-button-reminder" role="alert">
                Click the Invest button to save your investment!
              </div>
            )}
            <button
              className={`invest-button${isInvested ? ' invested' : ''}`}
              onClick={async () => {
                console.log('[Manual Investment] Button clicked');
                console.log('[Manual Investment] isInvested:', isInvested);
                console.log('[Manual Investment] disabled:', disabled);
                console.log('[Manual Investment] Current slider value:', sliderValue);
                await handleInvest();
              }}
              disabled={isInvested || isInvesting || disabled}
            >
              {isInvested ? '✓ Invested!' : isInvesting ? 'Investing...' : disabled ? 'Chat Period' : 'Invest'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePlay;
